import { useState, useRef, useEffect } from "react";
import ToggleSwitch from "./ToggleSwitch";

const inputClass =
  "w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black focus:bg-white transition-all duration-200";

const labelClass =
  "block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5";

const FieldGroup = ({ label, children }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {children}
  </div>
);

const typeOptions = [
  { value: "customer", label: "Customer Printer" },
  { value: "kitchen", label: "Kitchen Printer" },
];

const normalizePort = (value) => value.replace(/[^\d]/g, "");

const PrinterCard = ({ printer, index, onChange, onRemove, categories }) => {
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleChange = (field, value) => onChange(index, field, value);

  const showCategories = printer.type === "kitchen";
  const selectedIds = printer.categories || [];
  const selectedItems = (categories || []).filter((c) =>
    selectedIds.includes(c.key)
  );
  const displayText =
    selectedItems.length > 0
      ? selectedItems.map((c) => c.name).join(", ")
      : "";

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCategoriesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleCategory = (key) => {
    const next = selectedIds.includes(key)
      ? selectedIds.filter((id) => id !== key)
      : [...selectedIds, key];
    handleChange("categories", next);
  };

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-5">
      <div className="flex items-start justify-between gap-4 pb-4 border-b border-gray-100">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            {printer.name?.trim() ? printer.name : `Printer ${index + 1}`}
          </h3>
          <p className="text-xs text-gray-500">
            {printer.type === "kitchen" ? "Kitchen" : "Customer"} •{" "}
            {printer.ip || "IP not set"}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <ToggleSwitch
            checked={!!printer.isEnabled}
            onChange={(val) => handleChange("isEnabled", val)}
            labelOn="Enabled"
            labelOff="Disabled"
          />
          {/* <button
            type="button"
            onClick={() => onRemove(index)}
            className="px-3 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100 transition"
          >
            Remove
          </button> */}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
        <FieldGroup label="Name">
          <input
            type="text"
            value={printer.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="e.g. Kitchen Printer"
            className={inputClass}
          />
        </FieldGroup>

        <FieldGroup label="Type">
          <select
            value={printer.type}
            onChange={(e) => handleChange("type", e.target.value)}
            className={inputClass}
          >
            {typeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </FieldGroup>

        <FieldGroup label="IP Address">
          <input
            type="text"
            value={printer.ip}
            onChange={(e) => handleChange("ip", e.target.value)}
            placeholder="e.g. 192.168.1.50"
            className={inputClass}
          />
        </FieldGroup>

        <FieldGroup label="Port">
          <input
            type="text"
            inputMode="numeric"
            value={String(printer.port ?? "")}
            onChange={(e) => handleChange("port", normalizePort(e.target.value))}
            placeholder="e.g. 9100"
            className={inputClass}
          />
        </FieldGroup>

        {showCategories && (
          <div ref={dropdownRef}>
            <FieldGroup label="Categories (optional)">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setCategoriesOpen((o) => !o)}
                  className={`${inputClass} w-full text-left flex items-center justify-between gap-2 min-h-[42px] ${
                    !displayText ? "text-gray-400" : "text-black"
                  }`}
                >
                  <span className="truncate">
                    {displayText || "Select print category"}
                  </span>
                  <svg
                    className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${categoriesOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {categoriesOpen && (
                  <div className="absolute z-10 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg py-1 max-h-56 overflow-auto">
                    <div className="px-3 py-2 text-sm text-gray-400 cursor-not-allowed select-none" aria-disabled="true">
                      Select category
                    </div>
                    {(categories || []).map((cat) => (
                      <button
                        key={cat.key}
                        type="button"
                        onClick={() => toggleCategory(cat.key)}
                        className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                      >
                        <span
                          className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                            selectedIds.includes(cat.key)
                              ? "bg-blue-600 border-blue-600"
                              : "border-gray-300"
                          }`}
                        >
                          {selectedIds.includes(cat.key) && (
                            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </span>
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </FieldGroup>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrinterCard;

